import { ReactElement } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import { IDelayBase, ISelectedNode } from "../../../types";
import {
  formatDateTimeWithSeconds,
  formatTimeWithSeconds,
  getCurrentDatetimeInSeconds
} from "../../../system/utils";
import { getPalette } from "../../../configuration/configuration";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";

interface IInterruptionChartProps {
  beginShift: number;
  endShift?: number;
  title: string | number;
  delays: IDelayBase[];
  selected?: IDelayBase | null;
  setSelected?: (row: IDelayBase | null) => void;
  machines?: ISelectedNode[] | null;
  selectedMachine: string | null;
}


const DelayChart = ({ beginShift, endShift, delays, selected, setSelected, title, machines, selectedMachine }: IInterruptionChartProps): ReactElement => {
  const machineList = machines ? machines.filter(x => !selectedMachine || x.code === selectedMachine).map(y => y.code) : [ selectedMachine ];

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 120,
    marginBottom: 60,
    marginLeft: machineList.length === 1 ? 30 : 80,
    marginRight: 30,
    marginTop: 20
  });

  const chartID = useUniqueId("delay-chart");

  const xScale = d3.scaleLinear()
    .domain([beginShift, endShift ?? getCurrentDatetimeInSeconds()])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDelayBase) => xScale(d.endDatetime ?? getCurrentDatetimeInSeconds()) - xScale(d.beginDatetime);

  const machineHeight = dimensions.boundedHeight / machineList.length;

  const now = getCurrentDatetimeInSeconds();
  const xCursor = xScale(now);

  return (
    <div ref={ref}>
      <Chart dimensions={dimensions}  >
        <defs>
          <clipPath id={`cut-off-${chartID}`}>
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={`${title}`}
          ticks={getTicks(xScale.domain(), 5)}
          formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>

        { machineList.map((machine, index) =>
          <g key={`DelayLayerGantt-${machine}`} >
            {machineList.length > 1 &&
              <text x={-80} y={index * machineHeight + 12} fill={getPalette().statusColor.blueGray.background}>{machine}</text>
            }

            <rect
              style={{ fill: getPalette().statusColor.production.background }}
              x={0}
              y={index * machineHeight}
              width={Math.max(0, xCursor)}
              height={machineHeight - 2} />

            <DelayLayerGantt delays={delays.filter(x => x.production?.machine.code === machine)}
              height={machineHeight - 2} y={index * machineHeight} xAccessorScaled={xAccessorScaled}
              widthAccessorScaled={widthAccessorScaled} selected={selected} chartKey={chartID} setSelected={setSelected} />

            <rect
              style={{ fill: getPalette().statusColor.gray.background }}
              x={xCursor}
              y={index * machineHeight}
              width={Math.max(0, dimensions.boundedWidth - xCursor)}
              height={machineHeight - 2} />
          </g>
        )
        }

        <g clipPath={`cut-off-${chartID}`}>
          {(xCursor >= 0 || xCursor <= dimensions.boundedWidth) &&
          <>
            <line
              x1={xCursor}
              x2={xCursor}
              y1={0}
              y2={dimensions.boundedHeight}
              strokeWidth={2}
              stroke="#333"
              strokeDasharray="4"
            />

            <text
              fill="whitesmoke"
              x={xCursor + 2}
              y={10}
              fontSize=".8em"
              textAnchor={"start"}>
              Now
            </text>

          </>
          }
        </g>

      </Chart>
    </div>
  );
};

export default DelayChart;
